import {
	getCurrency,
	getEquipmentUniqueIdorElseId,
	getPropsFromObjectForHeaderKeys,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';

const UOM_VALUES = [
	{ label: 'each', value: 1 },
	{ label: 'box', value: 2 },
	{ label: 'pound', value: 3 },
];

const getUOMNameFromId = (id) =>
	nullSafeGetOrElse(
		'label',
		UOM_VALUES.find((_) => _.value === id),
		'each'
	);

const getCurrencyOptions = (currencies) =>
	currencies.map((c) => ({
		label: c,
		value: c,
	}));

// Mirrors the backend parts-level CSV export (openwrench-app#2280): part.labels carries the
// denormalized label names on the export/upload endpoints, joined with '; ' since label text
// may itself contain commas.
const getPartLabelNamesForExport = (part) => nullSafeGetOrElse('labels', part, []).join('; ');

export const PART_FLATFILE_HEADER: any[] = [
	{
		label: 'Record ID',
		key: 'id',
		type: 'string',
		alternates: ['Record ID', 'id'],
	},
	{
		label: 'OW Part ID',
		key: 'partId',
		type: 'string',
		alternates: ['OW Part ID', 'partId'],
		constraints: [{ type: 'required' }, { type: 'unique' }],
	},
	{
		label: 'Name',
		key: 'name',
		type: 'string',
		readonly: true,
		alternates: ['Part Name', 'name'],
	},
	{
		label: 'Part Number',
		key: 'partNumber',
		type: 'string',
		readonly: true,
		alternates: ['Part Number', 'partNumber'],
	},
	{
		label: 'Is Stocked',
		key: 'isStocked',
		type: 'boolean',
		alternates: ['Is Stocked', 'isStocked'],
		constraints: [{ type: 'required' }],
	},
	{
		label: 'On Hand Qty',
		key: 'onHandQuantity',
		type: 'number',
		alternates: ['Total Qty', 'totalQuantity', 'On Hand Qty', 'onHandQuantity'],
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Min Qty',
		key: 'minQuantity',
		type: 'number',
		alternates: ['Min Qty', 'minQuantity'],
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Max Qty',
		key: 'maxQuantity',
		type: 'number',
		alternates: ['Max Qty', 'maxQuantity'],
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Aisle',
		key: 'aisle',
		type: 'string',
	},
	{
		label: 'Bay',
		key: 'bay',
		type: 'string',
	},
	{
		label: 'Level',
		key: 'level',
		type: 'string',
	},
	{
		label: 'Bin',
		key: 'bin',
		type: 'string',
	},
	{
		// Download-only column: shown so exported files map cleanly on re-upload, but the
		// bulk-upload payload never sends labels — they cannot be updated via upload.
		label: 'Labels',
		key: 'labels',
		type: 'string',
		readonly: true,
		alternates: ['Labels', 'labels'],
	},
];

export const EQUIPMENT_FLATFILE_HEADER: any[] = [
	{
		label: 'Record ID',
		key: 'id',
		type: 'string',
	},
	{
		label: 'OW Equipment Type ID',
		key: 'equipmentTypeId',
		type: 'string',
		alternates: ['OW Equipment Type ID'],
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Name',
		key: 'name',
		type: 'string',
		readonly: true,
		alternates: ['Equipment Name'],
	},
	{
		label: 'Equipment Type #',
		key: 'equipmentTypeNumber',
		type: 'string',
		readonly: true,
		alternates: ['Equipment Type #'],
	},
	{
		label: 'Min Qty',
		key: 'minQuantity',
		type: 'number',
	},
	{
		label: 'Max Qty',
		key: 'maxQuantity',
		type: 'number',
		constraints: [{ type: 'required' }],
	},
];

export const getReceivedEquipmentFlatfileHeader = (
	allEquipmentTypesNames = [],
	allVendorNames = []
): any[] => [
	{
		label: 'Equipment ID',
		key: 'id',
		type: 'string',
	},
	{
		label: 'Equipment Type #',
		key: 'equipmentTypeId',
		type: 'enum',
		config: {
			options: allEquipmentTypesNames,
		},
		alternates: ['Equipment', 'equipmentTypeNumber'],
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Name',
		key: 'name',
		type: 'string',
		alternates: ['Equipment Name'],
	},
	{
		label: 'Serial Number',
		key: 'serialNumber',
		type: 'string',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Cost',
		key: 'cost',
		type: 'string',
	},
	{
		label: 'Is Rental',
		key: 'isRental',
		type: 'boolean',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Asset Number',
		key: 'assetNumber',
		type: 'string',
	},
	{
		label: 'Vendor',
		key: 'vendorId',
		type: 'enum',
		config: {
			options: allVendorNames,
		},
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Condition',
		key: 'condition',
		type: 'enum',
		config: {
			options: [
				{ label: 'Good', value: 'good' },
				{ label: 'Fair', value: 'fair' },
				{ label: 'Not Operational', value: 'notOperational' },
			],
		},
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Disposition Plan',
		key: 'dispositionPlan',
		alternates: ['Disposition Plan'],
		type: 'enum',
		config: {
			options: [
				{ label: 'Keep', value: 'keep' },
				{ label: 'Sell', value: 'sell' },
				{ label: 'Dispose', value: 'dispose' },
			],
		},
		constraints: [{ type: 'required' }],
	},
];

export const TRANSFER_TO_LOCATION_EQUIPMENT_FLATFILE_HEADER = (
	locations = [],
	assetTypes = [],
	assetModels = [],
	transferNoteMandatory = false
): any[] => [
	{
		label: 'Equipment #',
		key: 'id',
		type: 'string',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Location',
		key: 'locationId',
		type: 'enum',
		config: {
			options: locations,
		},
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Asset Type',
		key: 'assetTypeId',
		type: 'enum',
		config: {
			options: assetTypes,
		},
	},
	{
		label: 'Asset Number',
		key: 'assetNumber',
		type: 'string',
		constraints: [{ type: 'unique' }],
	},
	{
		label: 'Asset Model',
		key: 'assetModelId',
		type: 'enum',
		config: {
			options: assetModels,
		},
	},
	{
		label: 'Transfer Note',
		key: 'transferNote',
		type: 'string',
		constraints: [...(transferNoteMandatory ? [{ type: 'required' }] : [])],
	},
	{
		label: 'Effective Date (MM/DD/yyyy)',
		key: 'transferDate',
		type: 'string',
	},
];

export const TRANSFER_TO_STOCK_LOCATION_EQUIPMENT_FLATFILE_HEADER = (
	stockLocations = [],
	transferNoteMandatory
): any[] => [
	{
		label: 'Equipment #',
		key: 'id',
		type: 'string',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Destination Stock Location',
		key: 'stockLocationId',
		type: 'enum',
		config: {
			options: stockLocations,
		},
		alternates: ['Name', 'stock locations', 'locations'],
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Transfer Note',
		key: 'transferNote',
		type: 'string',
		constraints: [...(transferNoteMandatory ? [{ type: 'required' }] : [])],
	},
	{
		label: 'Effective Date (MM/DD/yyyy)',
		key: 'transferDate',
		type: 'string',
	},
];

export const PARTIAL_UPLOAD_CRUD_FIELD = {
	label: 'Action',
	key: 'crudAction',
	type: 'enum',
	config: {
		options: [
			{
				label: 'Create',
				value: 'create',
			},
			{
				label: 'Update',
				value: 'update',
			},
			{
				label: 'Delete',
				value: 'delete',
			},
		],
	},
	alternates: ['Action'],
	constraints: [{ type: 'required' }],
};

export const PART_MASTER_FLATFILE_HEADER = (
	currencies: string[],
	allVendors,
	glCodeEnabled,
	labelOptions: any[] = []
): any[] => [
	{
		label: 'OW Part ID',
		key: 'id',
		type: 'string',
		constraints: [
			{
				type: 'unique',
			},
		],
	},
	{
		label: 'Name',
		key: 'name',
		type: 'string',
		alternates: ['Part Name'],
		constraints: [
			{
				type: 'required',
			},
		],
	},
	{
		label: 'Part Number',
		key: 'partNumber',
		type: 'string',
		alternates: ['Part Number'],
		constraints: [
			{
				type: 'required',
			},
			{
				type: 'unique',
			},
		],
	},
	{
		label: 'Alias Part Number',
		key: 'aliasPartId',
		type: 'string',
		alternates: ['Alias Part Number'],
	},
	...(glCodeEnabled
		? [
				{
					label: 'GL Code',
					key: 'glCode',
					type: 'string',
					alternates: ['GL Code'],
				},
		  ]
		: []),
	{
		label: 'Description',
		key: 'description',
		type: 'string',
	},
	{
		label: 'Manufacturer Name',
		key: 'manufacturerName',
		type: 'string',
	},
	{
		label: 'Manufacturer ID',
		key: 'manufacturerId',
		type: 'string',
	},
	{
		label: 'Default Vendor',
		key: 'defaultVendorId',
		type: 'enum',
		alternates: ['Vendor', 'Vendor Name'],
		config: {
			options: allVendors,
		},
	},
	{
		label: 'Unit of Measure',
		key: 'uomId',
		type: 'enum',
		constraints: [{ type: 'required' }],
		config: {
			options: UOM_VALUES,
		},
	},
	{
		label: 'Cost',
		key: 'cost',
		type: 'number',
	},
	{
		label: 'Currency',
		key: 'currency',
		type: 'enum',
		config: {
			options: getCurrencyOptions(currencies),
		},
	},
	{
		label: 'Is Active',
		key: 'isActive',
		type: 'boolean',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Labels',
		key: 'labels',
		type: 'enum-list',
		config: {
			options: labelOptions,
		},
	},
];

export const PARTS_MASTER_LIST_PARTIAL_FLATFILE_HEADER = (
	currencies: string[],
	allVendors,
	glCodeEnabled,
	labelOptions: any[] = []
): any[] => [
	PARTIAL_UPLOAD_CRUD_FIELD,
	...PART_MASTER_FLATFILE_HEADER(currencies, allVendors, glCodeEnabled, labelOptions),
];

export const EQUIPMENT_MASTER_FLATFILE_HEADER = (
	currencies: string[],
	allVendors,
	glCodeEnabled
): any[] => [
	{
		label: 'OW Equipment Type ID',
		key: 'id',
		type: 'string',
	},
	{
		label: 'Model Name',
		key: 'modelName',
		type: 'string',
		alternates: ['Equipment Name'],
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Equipment Type #',
		key: 'equipmentTypeNumber',
		type: 'string',
		alternates: ['Equipment Type ID'],
		constraints: [{ type: 'unique' }],
	},
	...(glCodeEnabled
		? [
				{
					label: 'GL Code',
					key: 'glCode',
					type: 'string',
					alternates: ['GL Code'],
				},
		  ]
		: []),
	{
		label: 'Description',
		key: 'description',
		type: 'string',
	},
	{
		label: 'Manufacturer Name',
		key: 'manufacturerName',
		type: 'string',
	},
	{
		label: 'Manufacturer ID',
		key: 'manufacturerId',
		type: 'string',
	},
	{
		label: 'Default Vendor',
		key: 'defaultVendorId',
		type: 'enum',
		alternates: ['Vendor', 'Vendor Name'],
		config: {
			options: allVendors,
		},
	},
	{
		label: 'Cost',
		key: 'cost',
		type: 'number',
	},
	{
		label: 'Currency',
		key: 'currency',
		type: 'enum',
		config: {
			options: getCurrencyOptions(currencies),
		},
	},
	{
		label: 'Is Active',
		key: 'isActive',
		type: 'boolean',
		constraints: [{ type: 'required' }],
	},
];

export const EQUIPMENT_TYPE_MASTER_LIST_PARTIAL_FLATFILE_HEADER = (
	currencies: string[],
	allVendors,
	glCodeEnabled
): any[] => [
	PARTIAL_UPLOAD_CRUD_FIELD,
	...EQUIPMENT_MASTER_FLATFILE_HEADER(currencies, allVendors, glCodeEnabled),
];

export const getRecordForEquipmentLevel = (record = {}) => ({
	id: nullSafeGetOrElse('id', record, ''),
	equipmentTypeId: nullSafeGetOrElse('equipmentType.id', record, '--'),
	name: nullSafeGetOrElse('equipmentType.modelName', record, '--'),
	equipmentTypeNumber: nullSafeGetOrElse('equipmentType.equipmentTypeNumber', record, '--'),
	onHandQuantity: nullSafeGetOrElse('onHandQuantity', record, 0),
	minQuantity: nullSafeGetOrElse('minQuantity', record, 0),
	maxQuantity: nullSafeGetOrElse('maxQuantity', record, 0),
});

export const getFlatfileRecordForReceivedEquipment = (record = {}) => ({
	id: nullSafeGetOrElse('id', record, ''),
	equipmentTypeId: getEquipmentTypeValueField(nullSafeGetOrElse('equipmentType', record, {})),
	name: nullSafeGetOrElse('name', record, '--'),
	serialNumber: nullSafeGetOrElse('serialNumber', record, ''),
	cost: nullSafeGetOrElse('cost', record, ''),
	isRental: nullSafeGetOrElse('isRental', record, false),
	assetNumber: nullSafeGetOrElse('assetNumber', record, ''),
	vendorId: nullSafeGetOrElse('vendorId', record, ''),
	condition: nullSafeGetOrElse('condition', record, 'good'),
	dispositionPlan: nullSafeGetOrElse('dispositionPlan', record, 'keep'),
});

export const getFlatfileRecordForReceivedEquipmentExport = (record = {}, allVendors = []) => ({
	equipmentId: getEquipmentUniqueIdorElseId(record),
	equipmentTypeNumber: nullSafeGetOrElse('equipmentType.equipmentTypeNumber', record, ''),
	name: nullSafeGetOrElse('name', record, '--'),
	serialNumber: nullSafeGetOrElse('serialNumber', record, ''),
	cost: nullSafeGetOrElse('cost', record, ''),
	isRental: nullSafeGetOrElse('isRental', record, false),
	assetNumber: nullSafeGetOrElse('assetNumber', record, ''),
	vendorId: nullSafeGetOrElse(
		'name',
		allVendors.find((_) => _.id === nullSafeGet('vendorId', record)),
		''
	),
	condition: nullSafeGetOrElse('condition', record, 'good'),
	dispositionPlan: nullSafeGetOrElse('dispositionPlan', record, 'keep'),
});

export const getFlatfileRecordForTransferToLocationEquipment = (record = {}) => ({
	id: nullSafeGetOrElse('id', record, ''),
	locationId: nullSafeGetOrElse('asset.locationId', record, ''),
	assetTypeId: nullSafeGetOrElse('equipmentType.assetTypeId', record, ''),
	assetNumber: nullSafeGetOrElse('assetNumber', record, ''),
	assetModelId: nullSafeGetOrElse('modelName', record, ''),
	transferNote: nullSafeGetOrElse('transferNote', record, ''),
	transferDate: nullSafeGetOrElse('transferDate', record, ''),
});

export const getFlatFileRecordForPartLevel = (record = {}) => ({
	id: nullSafeGetOrElse('id', record, ''),
	partId: nullSafeGetOrElse('part.id', record, '--'),
	name: nullSafeGetOrElse('part.name', record, '--'),
	partNumber: nullSafeGetOrElse('part.partNumber', record, '--'),
	isStocked: nullSafeGetOrElse('isStocked', record, false),
	onHandQuantity: parseInt(nullSafeGetOrElse('onHandQuantity', record, 0)),
	minQuantity: parseInt(nullSafeGetOrElse('minQuantity', record, 0)),
	maxQuantity: parseInt(nullSafeGetOrElse('maxQuantity', record, 0)),
	aisle: nullSafeGetOrElse('aisle', record, ''),
	bay: nullSafeGetOrElse('bay', record, ''),
	level: nullSafeGetOrElse('level', record, ''),
	bin: nullSafeGetOrElse('bin', record, ''),
	labels: getPartLabelNamesForExport(nullSafeGet('part', record)),
});

export const getFlatfileRecordForPart = (
	record: any = {},
	currency,
	headerDefs: { key: string }[]
) => {
	const partRecord = {
		id: nullSafeGetOrElse('id', record, ''),
		name: nullSafeGetOrElse('name', record, ''),
		partNumber: nullSafeGetOrElse('partNumber', record, ''),
		aliasPartId: nullSafeGetOrElse('aliasPartId', record, ''),
		glCode: nullSafeGetOrElse('glCode', record, ''),
		description: nullSafeGetOrElse('description', record, ''),
		manufacturerName: nullSafeGetOrElse('manufacturerName', record, ''),
		manufacturerId: nullSafeGetOrElse('manufacturerId', record, ''),
		defaultVendorName: nullSafeGetOrElse('defaultVendor.name', record, ''),
		uomId: getUOMNameFromId(nullSafeGetOrElse('uomId', record, '')),
		cost: nullSafeGet('cost', record) ? parseFloat(nullSafeGet('cost', record)) : '',
		currency,
		isActive: nullSafeGetOrElse('isActive', record, false),
		labels:
			record.partLabels && record.partLabels.length
				? (Array.isArray(record.partLabels)
						? record.partLabels
								.map((label: any) =>
									typeof label === 'string' ? label : label.label || label.name || label
								)
								.filter(Boolean)
						: [
								typeof record.partLabels === 'string'
									? record.partLabels
									: record.partLabels.label || record.partLabels.name || record.partLabels,
						  ]
				  ).join(',')
				: '',
	};

	return getPropsFromObjectForHeaderKeys(partRecord, headerDefs);
};

export const getFlatfileRecordForEquipment = (
	record = {},
	currency,
	headerDefs: { key: string }[]
) => {
	const equipmentRecord = {
		id: nullSafeGetOrElse('id', record, ''),
		modelName: nullSafeGetOrElse('modelName', record, ''),
		equipmentTypeNumber: nullSafeGetOrElse('equipmentTypeNumber', record, ''),
		glCode: nullSafeGetOrElse('glCode', record, ''),
		description: nullSafeGetOrElse('description', record, ''),
		manufacturerName: nullSafeGetOrElse('manufacturerName', record, ''),
		manufacturerId: nullSafeGetOrElse('manufacturerId', record, ''),
		defaultVendorId: nullSafeGetOrElse('defaultVendorId', record, ''),
		cost: nullSafeGet('cost', record) ? parseFloat(nullSafeGet('cost', record)) : '',
		currency,
		isActive: nullSafeGetOrElse('isActive', record, false),
	};

	return getPropsFromObjectForHeaderKeys(equipmentRecord, headerDefs);
};

export const getStockLocationPartsLevelExportFields = (currentUser) => [
	{
		label: 'Record ID',
		key: 'id',
	},
	{
		label: 'OW Part ID',
		key: 'part.id',
	},
	{
		label: 'Part Name',
		key: 'part.name',
	},
	{
		label: 'Part Number',
		key: 'part.partNumber',
	},
	{
		label: 'Is Stocked',
		key: 'isStocked',
		default: false,
	},
	// This file is downloaded, edited and re-uploaded, so every column needs a default
	// that survives the round trip: getRecordForExport falls back to '--' otherwise, and
	// '--' is neither a valid location nor a parsable number on the way back in.
	{
		label: 'Aisle',
		key: 'aisle',
		default: '',
	},
	{
		label: 'Bay',
		key: 'bay',
		default: '',
	},
	{
		label: 'Level',
		key: 'level',
		default: '',
	},
	{
		label: 'Bin',
		key: 'bin',
		default: '',
	},
	{
		label: 'Min Qty',
		key: 'minQuantity',
		default: 0,
	},
	{
		label: 'Max Qty',
		key: 'maxQuantity',
		default: 0,
	},
	{
		label: 'Total Qty',
		key: 'onHandQuantity',
		default: 0,
	},
	{
		label: 'Requested Qty',
		key: 'requestedQuantity',
		default: 0,
	},
	{
		label: 'Available Qty',
		key: 'availableQuantity',
		default: 0,
		renderFn: (val) => (val < 0 ? 0 : val),
	},
	{
		label: 'Total Cost',
		key: 'totalStockedPartsCost',
		default: 0,
	},
	{
		label: 'Currency',
		key: 'currency',
		default: getCurrency({ currentUser }).id,
	},
	{
		label: 'Labels',
		key: 'part',
		default: {},
		renderFn: getPartLabelNamesForExport,
	},
];

export const getStockLocationEquipmentTypeLevelExportFields = (currentUser) => [
	{
		label: 'Equipment Type Name',
		key: 'equipmentType.modelName',
	},
	{
		label: 'Equipment Type Number',
		key: 'equipmentType.equipmentTypeNumber',
	},
	{
		label: 'Min Qty',
		key: 'minQuantity',
	},
	{
		label: 'Max Qty',
		key: 'maxQuantity',
	},
	{
		label: 'Total Qty',
		key: 'onHandQuantity',
		default: 0,
	},
	{
		label: 'Requested Qty',
		key: 'requestedQuantity',
		default: 0,
	},
	{
		label: 'Available Qty',
		key: 'onHandQuantity',
		default: 0,
	},
	{
		label: 'Total Cost',
		key: 'totalStockedEquipmentCost',
	},
	{
		label: 'Currency',
		key: 'currency',
		default: getCurrency({ currentUser }).id,
	},
];

export const getRecordForExport = (record, fields) =>
	fields.reduce((acc, field) => {
		const value = field.key
			? nullSafeGetOrElse(field.key, record, field.default !== undefined ? field.default : '--')
			: record;
		return {
			...acc,
			[field.label]: field.renderFn ? field.renderFn(value) : value,
		};
	}, {});

export const getEquipmentTypeValueField = (equipmentType) =>
	equipmentType.equipmentTypeNumber || equipmentType.id;

export const getEquipmentTypeFlatfileOptions = (equipmentTypes) =>
	equipmentTypes.map((_) => ({
		label: `${_.equipmentTypeNumber ? `${_.equipmentTypeNumber} - ` : ''}${_.modelName}`,
		value: getEquipmentTypeValueField(_),
		assetTypeId: _.assetTypeId,
	}));

export const getVendorFlatfileOptions = (vendors) =>
	vendors.map((_) => ({
		label: _.name,
		value: _.id,
	}));
